import { useEffect, useState } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import { Home, Package, BarChart3 } from 'lucide-react'
import { ForwardAuthProvider, useForwardAuth } from '@coherence/ui'
import Dashboard from './pages/Dashboard'
import Apps from './pages/Apps'
import AppDetail from './pages/AppDetail'
import AppDownload from './pages/AppDownload'
import Analytics from './pages/Analytics'
import CoherenceDownload from './pages/CoherenceDownload'
import PrivacyPolicy from './pages/PrivacyPolicy'
import Terms from './pages/Terms'
import LandingPage from './pages/LandingPage'
import { buildLoginUrl, shouldAutoRedirect } from './services/loginRedirect'

// Public routes that do NOT require authentication
// The root / and /apps show the public catalog; download pages and legal are always public.
const PUBLIC_PATHS = [
  '/',
  '/apps',
  '/download',
  '/harvey',
  '/mmb',
  '/danai',
  '/wedlegacy',
  '/sentinel',
  '/famulus',
  '/symphony',
  '/gc',
  '/wb2',
  '/phidias',
  '/coherence',
  '/get',
  '/privacy',
  '/terms',
]

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
}

// Forward-auth mode detection (same pattern as dashboard)
function useForwardAuthMode(): boolean {
  if (typeof window === 'undefined') return false
  const authMode = (import.meta as any).env?.VITE_AUTH_MODE
  if (authMode === 'forwardauth') return true
  if (authMode === 'oauth') return false
  const hostname = window.location.hostname
  return hostname.includes('ai4all') || hostname.includes('multiskilled.ai')
}

function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation()

  const navItems = [
    { path: '/', icon: Package, label: 'Apps' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/dashboard', icon: Home, label: 'Admin' },
  ]

  // Show nav on catalog pages (/ and /apps); hide on download landing and legal pages
  const DOWNLOAD_PATHS = ['/harvey', '/download', '/mmb', '/danai', '/wedlegacy', '/sentinel', '/famulus', '/symphony', '/gc', '/wb2', '/phidias', '/coherence', '/get', '/privacy', '/terms']
  const showNav = !DOWNLOAD_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))

  return (
    <div className="min-h-screen gradient-bg">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20">
        <div className="max-w-7xl mx-auto px-3 md:px-4 py-3 md:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <div className="w-8 md:w-10 h-8 md:h-10 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Package className="w-5 md:w-6 h-5 md:h-6" />
            </div>
            <div className="hidden sm:block">
              <span className="text-base md:text-xl font-bold">MSKAI AppStore</span>
              <p className="text-xs text-gray-400">Enterprise App Distribution</p>
            </div>
          </div>
          {showNav && (
            <nav className="flex gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-1 px-2 md:px-4 py-2 rounded-lg transition-colors min-h-[44px] md:min-h-auto flex-shrink-0 ${
                      isActive
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <item.icon className="w-5 h-5 md:w-4 md:h-4 flex-shrink-0" />
                    <span className="hidden sm:inline text-xs md:text-sm">{item.label}</span>
                  </Link>
                )
              })}
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6 text-center text-gray-500 text-sm">
        <p>MSKAI Enterprise AppStore v2.0.0</p>
        <p className="mt-1">Coherence Engine Ecosystem</p>
        <div className="mt-2 flex justify-center gap-4">
          <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms of Service</Link>
        </div>
      </footer>
    </div>
  )
}

function SmartRoot() {
  const { isAuthenticated, isLoading } = useForwardAuth()
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    )
  }
  if (isAuthenticated) return <Apps />
  return <LandingPage onLoginClick={() => { window.location.href = '/dashboard' }} />
}

/** Public routes — accessible without authentication */
function PublicRoutes() {
  return (
    <Routes>
      {/* Public catalog — root shows landing or app grid depending on auth state */}
      <Route path="/" element={<SmartRoot />} />
      <Route path="/apps" element={<Apps />} />
      {/* Branded download landing pages */}
      <Route path="/download/coherence-engine" element={<CoherenceDownload />} />
      <Route path="/download/:appId" element={<AppDownload />} />
      <Route path="/harvey" element={<AppDownload />} />
      <Route path="/mmb" element={<AppDownload />} />
      <Route path="/danai" element={<AppDownload />} />
      <Route path="/wedlegacy" element={<AppDownload />} />
      <Route path="/sentinel" element={<AppDownload />} />
      <Route path="/famulus" element={<AppDownload />} />
      <Route path="/symphony" element={<AppDownload />} />
      <Route path="/gc" element={<AppDownload />} />
      <Route path="/wb2" element={<AppDownload />} />
      <Route path="/phidias" element={<AppDownload />} />
      <Route path="/coherence" element={<CoherenceDownload />} />
      <Route path="/get" element={<CoherenceDownload />} />
      <Route path="/get/coherence-engine" element={<CoherenceDownload />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
    </Routes>
  )
}

/**
 * Shown on a protected path when /.auth/me reports no user. Traefik forward-auth should have redirected before the
 * page loaded; when it did not, send the visitor to the Authentik outpost once, and always offer a button.
 */
function SignInRequired() {
  const [redirecting, setRedirecting] = useState(false)
  const loginUrl = buildLoginUrl(window.location.href)

  useEffect(() => {
    let storage: Storage | null = null
    try {
      storage = window.sessionStorage
    } catch {
      storage = null
    }
    if (shouldAutoRedirect(storage, Date.now(), window.location.pathname)) {
      setRedirecting(true)
      window.location.assign(loginUrl)
    }
  }, [loginUrl])

  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <p className="text-gray-400 mb-4">Authentication required</p>
        <p className="text-sm text-gray-500 mb-4">
          {redirecting ? 'Redirecting you to sign in…' : 'Sign in to continue.'}
        </p>
        <a
          href={loginUrl}
          className="inline-block px-4 py-2 rounded-lg bg-primary-500 text-white hover:opacity-90 transition-opacity"
        >
          Sign in
        </a>
      </div>
    </div>
  )
}

/** Protected routes — require forward-auth authentication */
function ProtectedRoutes() {
  const { isAuthenticated, isLoading } = useForwardAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <SignInRequired />
  }

  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      {/* The nav "Admin" link and the landing page's sign-in both go to /dashboard; without this route a signed-in
          visitor got a header, an EMPTY main and a footer (AppStore verify, 2026-10-21). */}
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/apps" element={<Apps />} />
      <Route path="/apps/:appId" element={<AppDetail />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

function NotFound() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <p className="text-gray-400 mb-4">Page not found</p>
        <Link to="/" className="inline-block px-4 py-2 rounded-lg bg-primary-500 text-white hover:opacity-90">
          Back to apps
        </Link>
      </div>
    </div>
  )
}

function AppContent() {
  const location = useLocation()
  const isPublic = isPublicPath(location.pathname)

  return (
    <AppShell>
      {isPublic ? <PublicRoutes /> : <ProtectedRoutes />}
    </AppShell>
  )
}

function App() {
  const isForwardAuth = useForwardAuthMode()

  if (isForwardAuth) {
    return (
      <ForwardAuthProvider
        adminGroups={['coherence-admins', 'admins']}
        superuserGroups={['coherence-superusers']}
      >
        <AppContent />
      </ForwardAuthProvider>
    )
  }

  // Fallback for local dev (no forward-auth) — show everything
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Apps />} />
        <Route path="/apps" element={<Apps />} />
        <Route path="/apps/:appId" element={<AppDetail />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/download/coherence-engine" element={<CoherenceDownload />} />
        <Route path="/download/:appId" element={<AppDownload />} />
        <Route path="/harvey" element={<AppDownload />} />
        <Route path="/mmb" element={<AppDownload />} />
        <Route path="/danai" element={<AppDownload />} />
        <Route path="/wedlegacy" element={<AppDownload />} />
        <Route path="/sentinel" element={<AppDownload />} />
        <Route path="/famulus" element={<AppDownload />} />
        <Route path="/symphony" element={<AppDownload />} />
        <Route path="/gc" element={<AppDownload />} />
        <Route path="/wb2" element={<AppDownload />} />
        <Route path="/coherence" element={<CoherenceDownload />} />
        <Route path="/get" element={<CoherenceDownload />} />
        <Route path="/get/coherence-engine" element={<CoherenceDownload />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
      </Routes>
    </AppShell>
  )
}

export default App
